import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { img } from '../data/siteData';

function GalleryThumbnails({ images, activeIndex, onSelect, title, naturalRatio = false }) {
  const trackRef = useRef(null);
  const rowRef = useRef(null);
  const drag = useRef({ active: false, moved: false });
  const [isDragging, setIsDragging] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  const updateOverflow = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    setHasOverflow(track.scrollWidth - track.clientWidth > 1);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    const row = rowRef.current;
    if (!track || !row) return undefined;
    updateOverflow();
    const observer = new ResizeObserver(updateOverflow);
    observer.observe(track);
    observer.observe(row);
    return () => observer.disconnect();
  }, [images, updateOverflow]);

  useEffect(() => {
    const track = trackRef.current;
    const button = rowRef.current?.children[activeIndex];
    if (!track || !button) return;
    const viewport = track.getBoundingClientRect();
    const thumbnail = button.getBoundingClientRect();
    if (thumbnail.left < viewport.left) track.scrollLeft += thumbnail.left - viewport.left - 4;
    else if (thumbnail.right > viewport.right) track.scrollLeft += thumbnail.right - viewport.right + 4;
  }, [activeIndex, images]);

  const stepPhoto = (direction) => {
    onSelect((activeIndex + direction + images.length) % images.length);
  };

  const startDrag = (event) => {
    // Touch uses native scrolling; mouse capture starts only after a real drag
    // so a normal click still reaches the thumbnail button.
    if (event.pointerType !== 'mouse' || event.button !== 0 || !hasOverflow) return;
    drag.current = { active: true, moved: false, startX: event.clientX, scrollLeft: event.currentTarget.scrollLeft };
  };

  const stopDrag = (event) => {
    drag.current.active = false;
    setIsDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const moveDrag = (event) => {
    if (!drag.current.active) return;
    if ((event.buttons & 1) === 0) {
      stopDrag(event);
      return;
    }
    const distance = event.clientX - drag.current.startX;
    if (!drag.current.moved && Math.abs(distance) < 6) return;
    if (!drag.current.moved) {
      drag.current.moved = true;
      event.currentTarget.setPointerCapture(event.pointerId);
      setIsDragging(true);
    }
    event.preventDefault();
    event.currentTarget.scrollLeft = drag.current.scrollLeft - distance;
  };

  return (
    <div className={`gallery-thumbnails${naturalRatio ? ' natural-thumbnails' : ''}`}>
      <div
        ref={trackRef}
        className={`gallery-thumbnail-track${hasOverflow ? ' can-scroll' : ''}${isDragging ? ' dragging' : ''}`}
        role="region"
        aria-label={`${title} photo thumbnails`}
        tabIndex={0}
        onPointerDown={startDrag}
        onPointerMove={moveDrag}
        onPointerUp={stopDrag}
        onPointerCancel={stopDrag}
        onLostPointerCapture={stopDrag}
        onClickCapture={(event) => {
          if (drag.current.moved && event.detail !== 0) {
            event.preventDefault();
            event.stopPropagation();
            drag.current.moved = false;
          }
        }}
      >
        <div className="gallery-thumbnail-row" ref={rowRef}>
          {images.map((imagePath, index) => (
            <button
              type="button"
              key={imagePath}
              className={index === activeIndex ? 'active' : undefined}
              onClick={() => onSelect(index)}
              aria-label={`Show ${title} photo ${index + 1}`}
              aria-current={index === activeIndex ? 'true' : undefined}
            >
              <img loading="lazy" decoding="async" src={img(imagePath)} alt={`${title} supporting photo ${index + 1}`} draggable="false" onLoad={updateOverflow} />
            </button>
          ))}
        </div>
      </div>
      {images.length > 1 ? (
        <div className="cart-carousel-controls gallery-thumbnail-controls" aria-label={`${title} photo controls`}>
          <button type="button" onClick={() => stepPhoto(-1)} aria-label="Previous photo"><ChevronLeft size={20} /></button>
          <button type="button" onClick={() => stepPhoto(1)} aria-label="Next photo"><ChevronRight size={20} /></button>
        </div>
      ) : null}
    </div>
  );
}

export default GalleryThumbnails;
